import { Stack } from '@mui/material';
import React from 'react';

import { CheckIcon } from '@/shared/assets/icons/check-icon';
import {
  MainContent,
  MenuItemLabel,
  SelectablePlaceholder,
  StyledMenuItem,
} from '@/shared/components/ui/menu-item/menu-item.styled';
import { TMenuItemProps } from '@/shared/components/ui/menu-item/menu-item.types';
import { Tooltip } from '@/shared/components/ui/tooltip/tooltip';
import { useHasPermission } from '@/shared/features/permissions/hooks/use-permission-check/use-permission-check';
import { useTextOverflow } from '@/shared/hooks/use-text-overflow/use-text-overflow';

export const MenuItem: React.FC<TMenuItemProps> = ({
  variant,
  icon,
  selected,
  additionalInfo,
  children,
  tooltip,
  tooltipProps,
  permission,
  ...rest
}) => {
  const isSelectable = typeof selected === 'boolean';
  const hasPermission = useHasPermission(permission);

  // Only a string: element children lay themselves out as siblings in the row,
  // and one wrapper around them would stack them instead.
  const label = typeof children === 'string' ? children : undefined;
  const { ref, isOverflowing } = useTextOverflow<HTMLSpanElement>(label);

  // A clipped label is unreadable without this, and an item that already has
  // something to say keeps saying it underneath.
  const getTooltipTitle = () => {
    if (!isOverflowing) {
      return tooltip;
    }

    if (!tooltip) {
      return label;
    }

    return (
      <Stack gap="0.25rem">
        <span>{label}</span>
        {tooltip}
      </Stack>
    );
  };

  if (!hasPermission) {
    return null;
  }

  return (
    <Tooltip title={getTooltipTitle()} {...tooltipProps} wrapper="fullWidth">
      <StyledMenuItem $variant={variant} {...rest}>
        <MainContent $variant={variant}>
          {icon}
          {label ? <MenuItemLabel ref={ref}>{label}</MenuItemLabel> : children}
        </MainContent>
        {(additionalInfo || isSelectable) && (
          <Stack
            direction="row"
            alignItems="center"
            gap="0.5rem"
            flexShrink={0}
          >
            {additionalInfo}
            {isSelectable &&
              (selected ? (
                <CheckIcon variant="primary" />
              ) : (
                <SelectablePlaceholder />
              ))}
          </Stack>
        )}
      </StyledMenuItem>
    </Tooltip>
  );
};
